import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  X,
  Download,
  Share2,
  Scale,
  Sparkles,
  Trophy,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import ScorecardWizard from '../components/ScorecardWizard';
import { useCompare } from '../contexts/CompareContext';
import { products as allProducts, getProductsByCategory, getBrand } from '../data/products';
import {
  slugsToProducts,
  getBadges,
  buildEstimateUrl,
} from '../data/compareUtils';

// Spec rows displayed in the comparison grid
const COMPARE_ROWS = [
  { label: 'Tank Size', from: 'headlineSpec' },
  { label: 'COP', specRegex: /^COP$/i },
  { label: 'Refrigerant', specRegex: /refrigerant$/i },
  { label: 'Noise Level', specRegex: /noise/i },
  { label: 'Boost Element', specRegex: /backup|boost/i, fallback: 'Heat-pump only' },
  { label: 'Max Outlet Temp', specRegex: /outlet temperature|water outlet/i },
  { label: 'Operating Range', specRegex: /ambient/i },
  { label: 'Tank Warranty', from: 'warranty', component: /tank/i },
  { label: 'Compressor Warranty', from: 'warranty', component: /compressor/i },
  { label: 'Rebate Eligible', from: 'rebate' },
];

const getCellValue = (product, row) => {
  if (row.from === 'headlineSpec') {
    return `${product.headlineSpec.value} ${product.headlineSpec.unit}`;
  }
  if (row.from === 'warranty') {
    const w = product.warranty?.find((x) => row.component.test(x.component));
    return w?.term || '—';
  }
  if (row.from === 'rebate') {
    return product.rebateEligible ? 'Yes' : 'No';
  }
  const spec = product.specs?.find((s) => row.specRegex.test(s.label));
  return spec?.value || row.fallback || '—';
};

const ComparePage = () => {
  const { slugs, remove, clear, count, max, toggle } = useCompare();
  const [searchParams] = useSearchParams();
  const [showWizard, setShowWizard] = useState(false);

  // Hydrate from ?slugs= if compare is empty and URL has params
  const urlSlugs = useMemo(
    () => (searchParams.get('slugs') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const activeSlugs = slugs.length > 0 ? slugs : urlSlugs;
  const products = slugsToProducts(activeSlugs);
  const badges = useMemo(() => getBadges(products), [products]);

  const allHeatPumps = getProductsByCategory('heat-pumps');
  const remainingChoices = allHeatPumps.filter((p) => !activeSlugs.includes(p.slug));

  const handleShare = async () => {
    const url = `${window.location.origin}/compare?slugs=${encodeURIComponent(activeSlugs.join(','))}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Heat Pump Comparison · Supa Energy Hub',
          text: `Comparing ${products.map((p) => p.name).join(', ')}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        // eslint-disable-next-line no-alert
        alert('Comparison link copied to clipboard!');
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Compare Heat Pump Hot Water Models — Side-by-Side"
        description="Compare up to 4 heat pump hot water models side-by-side. Tank size, COP, refrigerant, noise, boost element, warranty — and get a personalised household-fit scorecard."
      />
      <Header />
      <main className="pb-24">
        {/* Hero */}
        <section className="pt-32 pb-10 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/products/heat-pumps" className="hover:text-[#3D5A47]">Heat Pumps</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#3D5A47] font-medium">Compare</span>
            </nav>

            <div className="flex items-end justify-between flex-wrap gap-4">
              <div className="max-w-3xl">
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                  Compare {count} of {max}
                </span>
                <h1 className="heading-1 text-[#3D5A47] mb-3">
                  Compare Heat Pump <span className="text-[#B5D43B]">Models</span>
                </h1>
                <p className="body-large text-gray-600">
                  Pick up to {max} models side-by-side. Tap "Find Your Best Match" for a personalised household-fit scorecard.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="gap-2"
                  disabled={products.length === 0}
                  data-testid="compare-share"
                >
                  <Share2 className="w-4 h-4" /> Share
                </Button>
                {products.length >= 2 && (
                  <Button
                    onClick={() => setShowWizard(true)}
                    className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2"
                    data-testid="open-scorecard"
                  >
                    <Sparkles className="w-4 h-4" /> Find Your Best Match
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {products.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-10 text-center" data-testid="compare-empty">
                <Scale className="w-14 h-14 mx-auto text-gray-300 mb-3" />
                <h2 className="text-xl font-bold text-[#3D5A47] mb-2">No models selected yet</h2>
                <p className="text-gray-600 mb-5">
                  Head back to the heat pump catalogue and tick the "Compare" checkbox on any product card to begin.
                </p>
                <Link to="/products/heat-pumps">
                  <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2">
                    Browse Heat Pumps <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Comparison table — spans 2 cols on lg */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="compare-table">
                      <thead>
                        <tr>
                          <th className="text-left text-xs uppercase tracking-wider text-gray-400 font-semibold p-4 bg-gray-50 sticky left-0 z-10">
                            Specification
                          </th>
                          {products.map((p) => (
                            <th key={p.slug} className="p-3 bg-gray-50 text-left min-w-[170px]">
                              <div className="flex justify-between items-start gap-2">
                                <div className="min-w-0 flex-1">
                                  <Link to={`/products/heat-pumps/${p.slug}`} className="block hover:opacity-80">
                                    <img src={p.image} alt={p.name} className="w-full h-20 object-contain bg-white rounded mb-2" loading="lazy" />
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                                      {getBrand(p.brand).name}
                                    </div>
                                    <div className="font-bold text-sm text-[#3D5A47] leading-tight line-clamp-2">
                                      {p.name.split(' (')[0]}
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">{p.model}</div>
                                  </Link>
                                  {(badges[p.slug] || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {badges[p.slug].map((b, bi) => (
                                        <span
                                          key={bi}
                                          className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-[#B5D43B] text-[#1a2820] rounded-full px-1.5 py-0.5"
                                          data-testid={`compare-badge-${p.slug}`}
                                        >
                                          <Trophy className="w-2.5 h-2.5" /> {b}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => remove(p.slug)}
                                  className="w-6 h-6 rounded hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-[#3D5A47] flex-shrink-0"
                                  aria-label={`Remove ${p.name}`}
                                  data-testid={`compare-table-remove-${p.slug}`}
                                >
                                  <X className="w-3.5 h-3.5 pointer-events-none" />
                                </button>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {COMPARE_ROWS.map((row, idx) => (
                          <tr key={row.label} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="px-4 py-3 text-xs text-gray-500 font-medium sticky left-0 bg-inherit">
                              {row.label}
                            </td>
                            {products.map((p) => (
                              <td key={p.slug} className="px-3 py-3 text-sm text-[#3D5A47] font-semibold">
                                {getCellValue(p, row)}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr>
                          <td className="px-4 py-3 text-xs text-gray-500 font-medium sticky left-0 bg-white">
                            Brochure
                          </td>
                          {products.map((p) => {
                            const url = getBrand(p.brand).brochureUrl;
                            return (
                              <td key={p.slug} className="px-3 py-3 text-sm">
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[#3D5A47] hover:text-[#B5D43B] font-medium"
                                  >
                                    <Download className="w-3.5 h-3.5" /> PDF
                                  </a>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* CTA bar */}
                  <div className="border-t border-gray-100 px-5 py-4 flex flex-wrap gap-3 items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Comparing {products.length} model{products.length === 1 ? '' : 's'}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={clear}
                        className="text-xs text-gray-500 hover:text-[#3D5A47] px-3 py-1"
                        data-testid="compare-table-clear"
                      >
                        Clear All
                      </button>
                      <Link to={buildEstimateUrl(activeSlugs)}>
                        <Button
                          className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 font-semibold"
                          data-testid="compare-cta-estimate"
                        >
                          Get Free Estimate <ArrowRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Sidebar: Scorecard wizard + add-more picker */}
                <aside className="space-y-5">
                  {products.length >= 2 ? (
                    <ScorecardWizard
                      products={products}
                      onClose={() => setShowWizard(false)}
                    />
                  ) : (
                    <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-5 text-center">
                      <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        Select at least 2 models to unlock the "Best Match" scorecard quiz.
                      </p>
                    </div>
                  )}

                  {/* Add more */}
                  {count < max && remainingChoices.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-4">
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">
                        Add up to {max - count} more
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {remainingChoices.map((p) => (
                          <button
                            key={p.slug}
                            onClick={() => toggle(p.slug)}
                            className="w-full text-left flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg"
                            data-testid={`compare-add-${p.slug}`}
                          >
                            <img src={p.image} alt={p.name} className="w-9 h-9 object-contain rounded bg-gray-50 flex-shrink-0" loading="lazy" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[#3D5A47] truncate">{p.name.split(' (')[0]}</p>
                              <p className="text-[10px] text-gray-400 font-mono truncate">{p.model}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </aside>
              </div>
            )}

            <div className="mt-8 text-xs text-gray-400 leading-relaxed max-w-3xl">
              Comparison values are drawn from each manufacturer's brochure or public technical sheet and may change without notice. "Best for..." winner badges are derived from the selected set only — they indicate the strongest performer **among the models you're currently comparing**, not the overall market. Always verify with the official brochure and your installer before purchase.
            </div>

            <div className="mt-6">
              <Link to="/products/heat-pumps" className="inline-flex items-center gap-1 text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B]">
                <ArrowLeft className="w-4 h-4" /> Back to all heat pumps
              </Link>
            </div>
          </div>
        </section>

        {/* Mobile-only inline scorecard modal toggled by FAB at top */}
        {showWizard && products.length >= 2 && (
          <div className="lg:hidden fixed inset-0 z-[60] bg-black/50 flex items-end" onClick={() => setShowWizard(false)}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-h-[90vh] overflow-y-auto bg-transparent">
              <ScorecardWizard products={products} onClose={() => setShowWizard(false)} />
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ComparePage;
